import {
  Category,
  CreateCategoryRequest,
  CreateExerciseRequest,
  CreateObjectiveAttributeRequest,
  CreateThemeRequest,
  CreateTherapyAttributeRequest,
  Exercise,
  ObjectiveAttribute,
  Theme,
  TherapyAttribute,
  UpdateCategoryRequest,
  UpdateExerciseRequest,
  UpdateObjectiveAttributeRequest,
  UpdateThemeRequest,
  UpdateTherapyAttributeRequest,
} from '@repo/types/src/glow-up';
import {
  createCategory,
  createExercise,
  createObjectiveAttribute,
  createTheme,
  createTherapyAttribute,
  deleteCategory,
  deleteExercise,
  deleteObjectiveAttribute,
  deleteTheme,
  deleteTherapyAttribute,
  getAllCategories,
  getAllExercises,
  getAllThemes,
  getCategoriesByTheme,
  getCategoryById,
  getExerciseById,
  getExercisesByCategory,
  getObjectiveAttributes,
  getThemeById,
  getTherapyAttributes,
  reorderCategories,
  reorderExercises,
  reorderObjectiveAttributes,
  reorderThemes,
  reorderTherapyAttributes,
  updateCategory,
  updateExercise,
  updateObjectiveAttribute,
  updateTheme,
  updateTherapyAttribute,
} from './glow-up.queries';
import { useMutation, useQuery } from '@tanstack/react-query';

import { glowUpQueryKeys } from './glow-up.query-keys';
import { queryClient } from '@/config/query-client.config';

// Theme Hooks
export const useGetThemes = (params?: { skip?: number; take?: number }) => {
  const {
    data: response,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...glowUpQueryKeys.themes.all, params],
    queryFn: () => getAllThemes(params),
  });

  return {
    themes: response?.data || [],
    count: response?.count || 0,
    isError,
    isLoading,
    refetch,
  };
};

export const useGetTheme = (id: number) => {
  const {
    data: theme,
    isError,
    isLoading,
  } = useQuery({
    queryKey: glowUpQueryKeys.themes.byId(id),
    queryFn: () => getThemeById(id),
    enabled: !!id,
  });

  return { theme, isError, isLoading };
};

export const useCreateTheme = () => {
  const mutation = useMutation({
    mutationFn: (payload: CreateThemeRequest) => createTheme(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: glowUpQueryKeys.themes.all });
    },
  });

  return mutation;
};

export const useUpdateTheme = () => {
  const mutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateThemeRequest;
    }) => updateTheme(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: glowUpQueryKeys.themes.all });
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.themes.byId(data.id),
      });
    },
  });

  return mutation;
};

export const useDeleteTheme = () => {
  const mutation = useMutation({
    mutationFn: (id: number) => deleteTheme(id),
    onSuccess: (data) => {
      queryClient.setQueryData<Theme[]>(glowUpQueryKeys.themes.all, (oldData) =>
        oldData?.filter((theme) => theme.id !== data.id),
      );
    },
  });

  return mutation;
};

// Category Hooks
export const useGetCategories = (params?: { skip?: number; take?: number }) => {
  const {
    data: response,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...glowUpQueryKeys.categories.all, params],
    queryFn: () => getAllCategories(params),
  });

  return {
    categories: response?.data || [],
    count: response?.count || 0,
    isError,
    isLoading,
    refetch,
  };
};

export const useGetCategoriesByTheme = (themeId: number) => {
  const {
    data: categories,
    isError,
    isLoading,
  } = useQuery({
    queryKey: glowUpQueryKeys.categories.byTheme(themeId),
    queryFn: () => getCategoriesByTheme(themeId),
    enabled: !!themeId,
  });

  return { categories, isError, isLoading };
};

export const useGetCategory = (id: number) => {
  const {
    data: category,
    isError,
    isLoading,
  } = useQuery({
    queryKey: glowUpQueryKeys.categories.byId(id),
    queryFn: () => getCategoryById(id),
    enabled: !!id,
  });

  return { category, isError, isLoading };
};

export const useCreateCategory = () => {
  const mutation = useMutation({
    mutationFn: (payload: CreateCategoryRequest) => createCategory(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.categories.all,
      });
    },
  });

  return mutation;
};

export const useUpdateCategory = () => {
  const mutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateCategoryRequest;
    }) => updateCategory(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.categories.all,
      });
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.categories.byId(data.id),
      });
    },
  });

  return mutation;
};

export const useDeleteCategory = () => {
  const mutation = useMutation({
    mutationFn: (id: number) => deleteCategory(id),
    onSuccess: (data) => {
      queryClient.setQueryData<Category[]>(
        glowUpQueryKeys.categories.all,
        (oldData) => oldData?.filter((category) => category.id !== data.id),
      );
    },
  });

  return mutation;
};

// Exercise Hooks
export const useGetExercises = (params?: { skip?: number; take?: number }) => {
  const {
    data: response,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...glowUpQueryKeys.exercises.all, params],
    queryFn: () => getAllExercises(params),
  });
  return {
    exercises: response?.data || [],
    count: response?.count || 0,
    isError,
    isLoading,
    refetch,
  };
};

export const useGetExercisesByCategory = (categoryId: number) => {
  const {
    data: exercises,
    isError,
    isLoading,
  } = useQuery({
    queryKey: glowUpQueryKeys.exercises.byCategory(categoryId),
    queryFn: () => getExercisesByCategory(categoryId),
    enabled: !!categoryId,
  });

  return { exercises, isError, isLoading };
};

export const useGetExercise = (id: number) => {
  const {
    data: exercise,
    isError,
    isLoading,
  } = useQuery({
    queryKey: glowUpQueryKeys.exercises.byId(id),
    queryFn: () => getExerciseById(id),
    enabled: !!id,
  });

  return { exercise, isError, isLoading };
};

export const useCreateExercise = () => {
  const mutation = useMutation({
    mutationFn: (payload: CreateExerciseRequest) => createExercise(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.exercises.all,
      });
    },
  });

  return mutation;
};

export const useUpdateExercise = () => {
  const mutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateExerciseRequest;
    }) => updateExercise(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.exercises.all,
      });
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.exercises.byId(data.id),
      });
    },
  });

  return mutation;
};

export const useDeleteExercise = () => {
  const mutation = useMutation({
    mutationFn: (id: number) => deleteExercise(id),
    onSuccess: (data) => {
      queryClient.setQueryData<Exercise[]>(
        glowUpQueryKeys.exercises.all,
        (oldData) => oldData?.filter((exercise) => exercise.id !== data.id),
      );
    },
  });

  return mutation;
};

// Objective Attribute Hooks
export const useGetObjectiveAttributes = () => {
  const {
    data: objectiveAttributes,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: glowUpQueryKeys.objectiveAttributes.all,
    queryFn: () => getObjectiveAttributes(),
  });

  return {
    objectiveAttributes: objectiveAttributes || [],
    isError,
    isLoading,
    refetch,
  };
};

export const useCreateObjectiveAttribute = () => {
  const mutation = useMutation({
    mutationFn: (payload: CreateObjectiveAttributeRequest) =>
      createObjectiveAttribute(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.objectiveAttributes.all,
      });
    },
  });

  return mutation;
};

export const useUpdateObjectiveAttribute = () => {
  const mutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateObjectiveAttributeRequest;
    }) => updateObjectiveAttribute(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.objectiveAttributes.all,
      });
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.objectiveAttributes.byId(data.id),
      });
    },
  });

  return mutation;
};

export const useDeleteObjectiveAttribute = () => {
  const mutation = useMutation({
    mutationFn: (id: number) => deleteObjectiveAttribute(id),
    onSuccess: (data) => {
      queryClient.setQueryData<ObjectiveAttribute[]>(
        glowUpQueryKeys.objectiveAttributes.all,
        (oldData) =>
          oldData?.filter((attribute) => attribute.id !== data.id) || [],
      );
    },
  });

  return mutation;
};

// Therapy Attribute Hooks
export const useGetTherapyAttributes = () => {
  const {
    data: therapyAttributes,
    isError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: glowUpQueryKeys.therapyAttributes.all,
    queryFn: () => getTherapyAttributes(),
  });

  return {
    therapyAttributes: therapyAttributes || [],
    isError,
    isLoading,
    refetch,
  };
};

export const useCreateTherapyAttribute = () => {
  const mutation = useMutation({
    mutationFn: (payload: CreateTherapyAttributeRequest) =>
      createTherapyAttribute(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.therapyAttributes.all,
      });
    },
  });

  return mutation;
};

export const useUpdateTherapyAttribute = () => {
  const mutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdateTherapyAttributeRequest;
    }) => updateTherapyAttribute(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.therapyAttributes.all,
      });
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.therapyAttributes.byId(data.id),
      });
    },
  });

  return mutation;
};

export const useDeleteTherapyAttribute = () => {
  const mutation = useMutation({
    mutationFn: (id: number) => deleteTherapyAttribute(id),
    onSuccess: (data) => {
      queryClient.setQueryData<TherapyAttribute[]>(
        glowUpQueryKeys.therapyAttributes.all,
        (oldData) =>
          oldData?.filter((attribute) => attribute.id !== data.id) || [],
      );
    },
  });

  return mutation;
};

// Reorder Hooks
export const useReorderThemes = () =>
  useMutation({
    mutationFn: (items: { id: number; position: number }[]) =>
      reorderThemes(items),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: glowUpQueryKeys.themes.all }),
  });

export const useReorderCategories = () =>
  useMutation({
    mutationFn: (items: { id: number; position: number }[]) =>
      reorderCategories(items),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.categories.all,
      }),
  });

export const useReorderExercises = () =>
  useMutation({
    mutationFn: (items: { id: number; position: number }[]) =>
      reorderExercises(items),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.exercises.all,
      }),
  });

export const useReorderObjectiveAttributes = () =>
  useMutation({
    mutationFn: (items: { id: number; position: number }[]) =>
      reorderObjectiveAttributes(items),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.objectiveAttributes.all,
      }),
  });

export const useReorderTherapyAttributes = () =>
  useMutation({
    mutationFn: (items: { id: number; position: number }[]) =>
      reorderTherapyAttributes(items),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: glowUpQueryKeys.therapyAttributes.all,
      }),
  });
